import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup
} from "@mui/material";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

export const HOSTED_NIGHTSCOUT_MONTHLY_EUR = 4.99;
export const HOSTED_NIGHTSCOUT_AICHAT_MONTHLY_EUR = 9.99;

export type HostedNightscoutBillingCycle = "monthly" | "yearly";

interface HostedNightscoutBillingCycleRadiosProps {
  value: HostedNightscoutBillingCycle;
  onChange: (cycle: HostedNightscoutBillingCycle) => void;
  /** MUI RadioGroup `row` — default true */
  row?: boolean;
  sx?: SxProps<Theme>;
}

export function HostedNightscoutBillingCycleRadios({
  value,
  onChange,
  row = true,
  sx,
}: HostedNightscoutBillingCycleRadiosProps) {
  const { t } = useTranslation();

  return (
    <FormControl component="fieldset" sx={sx}>
      <RadioGroup
        row={row}
        name="hosted-nightscout-billing-cycle"
        value={value}
        onChange={(e) =>
          onChange(e.target.value as HostedNightscoutBillingCycle)
        }
        sx={{
          justifyContent: "center",
          flexWrap: "wrap",
          gap: row ? 2 : 0,
        }}
      >
        <FormControlLabel
          value="monthly"
          control={<Radio />}
          label={t("Monthly payment")}
        />
        <FormControlLabel
          value="yearly"
          control={<Radio />}
          label={t("Yearly Payment")}
        />
      </RadioGroup>
    </FormControl>
  );
}

interface HostedNightscoutPriceDisplayProps {
  billingCycle: HostedNightscoutBillingCycle;
  /** Typography variant for main price line */
  variant?: "h5" | "h6";
  monthlyEur?: number;
}

export function HostedNightscoutPriceDisplay({
  billingCycle,
  variant = "h6",
  monthlyEur = HOSTED_NIGHTSCOUT_MONTHLY_EUR,
}: HostedNightscoutPriceDisplayProps) {
  const { t } = useTranslation();

  if (billingCycle === "yearly") {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant={variant}
          sx={{
            color: "text.primary",
            fontWeight: "bold",
          }}
        >
          €{(12 * monthlyEur).toFixed(2)} / {t("year")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5 }}
        >
          12 × €{monthlyEur.toFixed(2)} {t("/month")}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      variant={variant}
      sx={{
        color: "text.primary",
        fontWeight: "bold",
        textAlign: "center",
      }}
    >
      €{monthlyEur.toFixed(2)} {t("/month")}
    </Typography>
  );
}
